


import Component from 'vue-class-component';
import {Vue, Watch} from 'vue-property-decorator';
import {Validations} from 'vuelidate-property-decorators';
import {TVuelidateRuleSet} from '@/_types/vuelitation-rule-set.type';
import {required} from 'vuelidate/lib/validators';
import EventLanguageSelector from '@/_components/create-event/event-language-selector.vue';
import _cloneDeep from 'lodash.clonedeep';
import DateTimeHelper from '@/_helpers/date-time.helper';
import {EventAccessType, TEvent} from '@/_types/event.type';
import {TAbbreviations, TLanguageName} from '@/_modules/events/types/event.type';
import timezone from '@/_modules/events/components/timezones.json';
import { TTimezoneInfo } from '@/_types/timezone-info.type';
import { TranslateResult } from 'vue-i18n';

type TCreateEventFake = {
  title: string;
  date_start: Date;
  date_end: Date;
  access_type: EventAccessType;
  languages: string[];
  time_region: string;
}

let prevStartDate: Date = null;

const isValidStartDateNotInPast = (startDate: Date): boolean => { // TODO: move this to helper
  if (startDate === null || startDate === undefined) {
    return true;
  }

  const currentDate = new Date().getTime() / 1000;
  const dateStart = new Date(startDate).getTime() / 1000;

  prevStartDate = startDate;

  return +currentDate < dateStart;
};

const isValidEndDateNotInPast = (endDate: Date): boolean => { // TODO: move this to helper

  if ((prevStartDate === null || prevStartDate === undefined) && (endDate === null || endDate === undefined)) {
    return true;
  }

  if (prevStartDate && endDate) {
    const _startDate: number = new Date(prevStartDate).getTime() / 1000;
    const _dateEnd: number = new Date(endDate).getTime() / 1000;

    return _dateEnd > _startDate;
  } else {
    return true;
  }
};

@Component({
  name: 'create-event',
  components: {EventLanguageSelector},
  computed: {
  },
})

export default class CreateEvent extends Vue {

  @Validations()
  public readonly validations: TVuelidateRuleSet<TCreateEventFake> = {
    formData: {
      title: {
        required,
      },
      date_start: {
        required,
        isValidStartDate(date_start: Date): boolean {
          return isValidStartDateNotInPast(date_start);
        }
      },
      date_end: {
        required,
        isValidEndDate(date_end: Date): boolean {
          return isValidEndDateNotInPast(date_end);
        }
      },
      time_region: {
        required,
      }
    },
  }
  public timezone: TTimezoneInfo[] = timezone;

  public formData: TCreateEventFake = {
    title: '',
    date_start: null,
    date_end: null,
    access_type: EventAccessType.REGISTER,
    languages: ['en'],
    time_region: ''
  };
  public endDateMinimum: Date = new Date()

  public isShowStartDate: boolean = false
  public isShowEndDate: boolean = false
  public isShowTimezoneLocation: boolean = false
  public isMultipleLanguages: boolean = false
  public isSendSuccess: boolean = false
  public createdHintText: TranslateResult = ''
  public timezoneLocation: TTimezoneInfo = {} as TTimezoneInfo;
  public isSaveEventButtonDisabled: boolean = false;

  @Watch('timezoneLocation', {immediate: true})
  private onTimezoneLocationChanged(): void {
    if(this.timezoneLocation && !this.timezoneLocation.utc) { return; }
    this.formData.time_region = this.timezoneLocation.utc[0];
  }

  private autoSetEndDate(val: Date): void {
    const start: number = new Date(this.formData.date_start).getTime() / 1000;
    const end: number = new Date(this.formData.date_end).getTime() / 1000;
    // minimum end date is 1 hour ahead, used in the :min-date prop
    this.endDateMinimum = new Date(this.formData.date_start.getTime() + 60 * 60 * 1000);

    // val has to be present and be a JS Date object. Simple check using getTime
    if (val && ('getTime' in val)) {
      if (!this.formData.date_end || start > end) {
        const date_end = new Date(val);
        date_end.setHours(23, 59, 59);

        // If auto-set date_end time is closer than 1 hour, move it to next day 23:59
        if (date_end.getTime() - val.getTime() < (60 * 60 * 1000)) {
          date_end.setTime(date_end.getTime() + 1000 * 60 * 60 * 24);
        }

        this.formData.date_end = date_end;
      }
    }
  }

  private checkEndDate(val: Date): void {

    // Set hours to date_start.hours + 1
    if (val.getTime() - +this.formData.date_start < 60 * 60 * 1000) {
      this.formData.date_end = new Date(this.formData.date_start.getTime() + 60 * 60 * 1000);
    }
  }

  private getCalendarDateTimeFormat(): any {
    const lang = this.$route.params.lang;
    let textData: TAbbreviations;

    const textStrings: TLanguageName = {
      zh: {
        firstDay: 0,
        dayAbbreviation: ['日', '一', '二', '三', '四', '五', '六'],
        dayList: ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'],
        monthList: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'],
        monthLongList: ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月']
      },
      en: {
        firstDay: 0,
        dayAbbreviation: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
        dayList: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        monthList: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        monthLongList: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      },
      lt: {
        firstDay: 1,
        dayAbbreviation: ['S.', 'P.', 'A.', 'T.', 'K.', 'Pn.', 'Š.'],
        dayList: ['S.', 'P.', 'A.', 'T.', 'K.', 'Pn.', 'Š.'],
        monthList: ['Saus', 'vas', 'kovas', 'bal', 'geg', 'birz', 'liepa', 'rugp', 'rugs', 'spalis', 'lapkr', 'gr'],
        monthLongList: ['Sausis', 'Vasaris', 'Kovas', 'Balandis', 'Gegužė', 'Birželis', 'Liepa', 'Rugpjūtis', 'Rugsėjis', 'Spalis', 'Lapkritis', 'Gruodis'],
      },
      ru: {
        firstDay: 1,
        dayAbbreviation: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        dayList: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        monthList: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'],
        monthLongList: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
      },
      uk: {
        firstDay: 1,
        dayAbbreviation: ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        dayList: ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        monthList: ['Січ', 'Лют', 'Берез', 'Квіт', 'Трав', 'Черв', 'Лип', 'Серп', 'Верес', 'Жовт', 'Листоп', 'Груд'],
        monthLongList: ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'],
      }
    };

    if (!textStrings[lang]) {
      textData = textStrings.en;
    } else {
      textData = textStrings[lang];
    }

    return {
      formatDisplay(date: Date): string {
        return `${textData.dayList[date.getDay()]}, ${textData.monthList[date.getMonth()]} ${date.getDate()}`;
      },
      formatMonth(date: Date): string {
        return `${textData.monthLongList[date.getMonth()]} ${date.getFullYear()}`;
      },
      getWeekDayArray(): string[] {
        const firstDayOfWeek = textData.firstDay ? textData.firstDay : 0; // firstDay or fallback to Sunday
        const beforeArray: string[] = [];
        const afterArray: string[] = [];
        for (let i = 0; i < textData.dayAbbreviation.length; i++) {
          if (i < firstDayOfWeek) {
            afterArray.push(textData.dayAbbreviation[i]);
          } else {
            beforeArray.push(textData.dayAbbreviation[i]);
          }
        }

        return beforeArray.concat(afterArray);
      },
      getMonthList(): string[] {
        return textData.monthList;
      },
      formatDateDisplay(date: Date): string {
        return `${date.getDate()} ${textData.monthList[date.getMonth()]}`;
      },
    };
  }
  private firstDayOfWeek(): number {
    if(this.$i18n.locale === 'ru' || this.$i18n.locale === 'lt' || this.$i18n.locale === 'uk') {
      return 1;
    } else {
      return 0;
    }
  }

  private handleEventLanguagesUpdated(eventData: string[]): void {
    this.formData.languages = _cloneDeep(eventData);
  }

  private showMultipleLanguages(event: Event): void {
    this.isMultipleLanguages = (event.target as HTMLInputElement).value === 'show';
  }

  private async createEvent (): Promise<TEvent | void> {

    this.isSaveEventButtonDisabled = true;

    this.$v.formData.$touch();

    if(!this.formData) {
      this.isSaveEventButtonDisabled = false;
      return;
    }

    const createData = Object.assign({}, this.formData, {
      date_start: DateTimeHelper.dateToApiDate(this.formData.date_start),
      date_end: DateTimeHelper.dateToApiDate(this.formData.date_end),
    });

    const result = await this.$store.dispatch('_eventStore/createEvent', createData);
    if (result) {
      this.$v.$reset();
      this.isSendSuccess = true;
      this.createdHintText = this.$t('eventPage.create.createSuccess');

      this.$router.push({ name: 'event-info', params: { eventId: '' + result.id } });
    } else {
      this.isSendSuccess = false;
      this.createdHintText = this.$t('eventPage.create.createError');
      this.isSaveEventButtonDisabled = false;
    }

  }
}
